import {
  useCallback, useMemo,
} from 'react';
import { flatten } from '~/_shared/utils/collections/collections';
import { useTranslation } from '~/_shared/utils/hooks';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { memoizeOne } from '~/_shared/utils/memoize/memoize';
import { useSelector } from '../../_shared/utils/hooks/useSelector';
import { sortAlphabetically } from '../../_shared/utils/sort';
import {
  type BoundaryGroup, type BoundaryGroupBase,
} from '../../boundary/boundary.types';
import { type AppState } from '../app.store';
import type { BoundaryGroupId } from '../boundaries/boundaryIdentifier.type';
import {
  useClientMembers, useUserIdSelector,
} from '../userData/userData.selectors';
import type {
  BoundaryGroupDemographic, DemographicIdCode,
} from './boundaryGroups.repository';

export const boundaryGroupsSelector = (state: AppState) => state.boundaries.groups.groups;
export const useBoundaryGroupsSelector = () => useSelector(boundaryGroupsSelector);

const listedBoundaryGroupsSelectorMemoized = memoizeOne((groups: ReadonlyArray<BoundaryGroup>) => groups.filter(g => g.visible));
export const listedBoundaryGroupsSelector = (state: AppState) => listedBoundaryGroupsSelectorMemoized(boundaryGroupsSelector(state));
export const useListedBoundaryGroupsSelector = () => useSelector(listedBoundaryGroupsSelector);

const boundaryGroupsIsLoadingSelector = (state: AppState) => state.boundaries.groups.isLoading;
export const useBoundaryGroupsIsLoadingSelector = () => useSelector(boundaryGroupsIsLoadingSelector);

const boundaryGroupsIsListingLoadingSelector = (state: AppState) => state.boundaries.groups.isListingLoading;
export const useBoundaryGroupsIsListingLoadingSelector = () => useSelector(boundaryGroupsIsListingLoadingSelector);

export const useGlobalBoundaryGroups = () => {
  const boundaryGroups = useListedBoundaryGroupsSelector();

  return useMemo(() => {
    return boundaryGroups.filter(group => group.clientId === null);
  }, [boundaryGroups]);
};

export const boundaryGroupSelector = createStateSelector([
  boundaryGroupsSelector,
  (_state, boundaryGroupId: Maybe<BoundaryGroupId>) => boundaryGroupId,
], (boundaryGroups, boundaryGroupId) => {
  return boundaryGroups.find((group) => group.id === boundaryGroupId);
});

export const useBoundaryGroupSelector = (boundaryGroupId: Maybe<BoundaryGroupId>) => {
  return useSelector((state) => boundaryGroupSelector(state, boundaryGroupId));
};

export const checkBoundaryIsCustom = (group: Maybe<BoundaryGroupBase>) => {
  return !!group && group.clientId !== null;
};

export const boundaryGroupIsCustomSelector = createStateSelector(
  boundaryGroupsSelector,
  (_state, boundaryGroupId: Maybe<BoundaryGroupId>) => boundaryGroupId,
  (boundaryGroups, boundaryGroupId: Maybe<BoundaryGroupId>) =>
    checkBoundaryIsCustom(findBoundaryGroup(boundaryGroups, boundaryGroupId))
);

export const useCheckBoundaryIsCustom = () => {
  const { getBoundaryGroupById } = useGetBoundaryGroup();

  const checkCb = useCallback((boundaryGroupId: Maybe<BoundaryGroupId>) => {
    const boundaryGroup = getBoundaryGroupById(boundaryGroupId);

    if (!boundaryGroup) {
      return false;
    }

    return checkBoundaryIsCustom(boundaryGroup);
  }, [getBoundaryGroupById]);

  return {
    checkBoundaryIsCustom: checkCb,
  };
};

export const useBoundaryIsCustom = (boundaryGroupId: Maybe<BoundaryGroupId>) => {
  const { checkBoundaryIsCustom } = useCheckBoundaryIsCustom();

  return useMemo(() => checkBoundaryIsCustom(boundaryGroupId),
    [boundaryGroupId, checkBoundaryIsCustom]);
};

export const useCheckBoundaryGroupPermissions = () => {
  const [t] = useTranslation();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const userId = useUserIdSelector();
  const clientMembers = useClientMembers();

  const checkCb = useCallback((boundaryGroupId: Maybe<BoundaryGroupId>) => {
    const boundaryGroup = getBoundaryGroupById(boundaryGroupId);

    const boundaryIsCustom = checkBoundaryIsCustom(boundaryGroup);
    const boundaryOwnerId = boundaryGroup?.userId ?? null;
    const boundaryIsEditable = boundaryIsCustom && !!userId && boundaryOwnerId === userId;

    const boundaryOwner = boundaryOwnerId
      ? { boundaryOwnerId, boundaryOwnerName: clientMembers.find((member) => member.id === boundaryOwnerId)?.name ?? t('Unknown') }
      : { boundaryOwnerId: null, boundaryOwnerName: null };

    return {
      boundaryIsCustom,
      boundaryIsEditable,
      ...boundaryOwner,
    };
  }, [clientMembers, getBoundaryGroupById, t, userId]);

  return {
    checkBoundaryGroupPermissions: checkCb,
  };
};

export const useBoundaryGroupPermissions = (boundaryGroupId: Maybe<BoundaryGroupId>) => {
  const { checkBoundaryGroupPermissions } = useCheckBoundaryGroupPermissions();

  return useMemo(() => checkBoundaryGroupPermissions(boundaryGroupId),
    [boundaryGroupId, checkBoundaryGroupPermissions]);
};

export type BoundaryGroupPermissions = ReturnType<typeof useBoundaryGroupPermissions>;

export const useIsBoundaryAiGenerated = (boundaryGroupId: number) => {
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  return (getBoundaryGroupById(boundaryGroupId)?.wms.territoriesCount ?? 0) > 0;
};

export const useCustomBoundaryGroupsSelector = () => {
  const boundaryGroups = useListedBoundaryGroupsSelector();

  return useMemo(() => {
    return boundaryGroups
      .filter(group => checkBoundaryIsCustom(group))
      .sort((a, b) => sortAlphabetically(a.name, b.name));
  }, [boundaryGroups]);
};

export const selectAllDemographicsMap = createStateSelector([
  boundaryGroupsSelector,
], (boundaryGroups) => {
  const entries = flatten(boundaryGroups.map((group) => {
    if (group.demographics) {
      return group.demographics.map(demographic => (
        [demographic.id, demographic] as const
      ));
    }
    return [];
  }));
  return new Map(entries) as ReadonlyMap<DemographicIdCode, BoundaryGroupDemographic>;
});

export const useSelectAllDemographicsMap = () => useSelector(selectAllDemographicsMap);

// #region Get Boundary Group by ID

const findBoundaryGroup = (boundaryGroups: readonly BoundaryGroup[], boundaryGroupId: Maybe<BoundaryGroupId>) =>
  boundaryGroups.find(boundaryGroup => boundaryGroup.id === boundaryGroupId);

export const findEditBoundaryGroup = (boundaryGroups: readonly BoundaryGroup[], boundaryGroupId: Maybe<BoundaryGroupId>) => {
  const original = findBoundaryGroup(boundaryGroups, boundaryGroupId);
  const editFallback = findBoundaryGroup(boundaryGroups, original?.editBoundaryGroupFallback);

  return editFallback ?? original;
};

export const useGetBoundaryGroup = () => {
  const boundaryGroups = useBoundaryGroupsSelector();

  const getBoundaryGroupById = useCallback((boundaryGroupId: Maybe<BoundaryGroupId>) =>
    findBoundaryGroup(boundaryGroups, boundaryGroupId),
  [boundaryGroups]);

  return {
    getBoundaryGroupById,
  };
};

// #endregion
